/**
 * First-run onboarding: shown when the signed-in user belongs to zero
 * companies. Wraps the rest of the SPA — the dashboard would just render
 * an empty fleet with no actionable UI otherwise.
 *
 * On success: invalidates the companies query, marks the new company
 * active in the UI store, and dismisses by re-rendering (the parent's
 * empty-companies check flips to false).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { companiesApi } from "../api/queries/companies";
import { useUiStore } from "../stores/ui";
import { ApiError } from "../api/client";

export function OnboardingCompany() {
  const qc = useQueryClient();
  const setActiveCompanyId = useUiStore((s) => s.setActiveCompanyId);
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => companiesApi.create({ name: name.trim() }),
    onSuccess: (company) => {
      setActiveCompanyId(company.id);
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e: ApiError) =>
      setErr(e.message ?? "Could not create company"),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) {
      setErr("Give the company a name");
      return;
    }
    create.mutate();
  };

  return (
    <main className="min-h-screen grid place-items-center bg-canvas px-4">
      <div className="card w-full max-w-[460px] p-6">
        <header className="mb-4">
          <h1 className="text-[18px] font-semibold tracking-[-0.01em]">
            Create your first company
          </h1>
          <p className="mt-1 text-[12px] text-secondary">
            A company groups your paired computers, agents, and work orders.
            You can rename or delete it later in Settings.
          </p>
        </header>
        <form onSubmit={onSubmit} className="grid gap-3" noValidate>
          <label className="field">
            <span className="field__label">Company name</span>
            <input
              type="text"
              autoFocus
              className="input"
              placeholder="e.g. Acme Robotics"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          {err && (
            <div role="alert" className="rounded-sm bg-status-error/10 px-3 py-2 text-[12px] text-status-error">
              {err}
            </div>
          )}
          <button
            className="btn btn--primary btn--lg"
            type="submit"
            disabled={create.isPending}
          >
            {create.isPending ? "Creating…" : "Create company"}
          </button>
        </form>
      </div>
    </main>
  );
}
